import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin, ArrowRight } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    { name: 'Startup Services', path: '/startup-services' },
    { name: 'GST Services', path: '/gst' },
    { name: 'Registration', path: '/registration' },
    { name: 'Compliances', path: '/compliances' },
  ];

  const popularServices = [
    { name: 'Private Limited Registration', path: '/startup-services' },
    { name: 'GST Registration', path: '/gst' },
    { name: 'Trademark Registration', path: '/registration' },
    { name: 'Income Tax Filing', path: '/compliances' },
  ];

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '+91 98206 69943',
      link: 'tel:+919820669943',
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'info@gklegalsolutions.com',
      link: 'mailto:info@gklegalsolutions.com',
    },
    {
      icon: Clock,
      title: 'Working Hours',
      content: 'Mon - Sat: 10:30 AM - 7:00 PM',
      link: null,
    },
    {
      icon: MapPin,
      title: 'Office',
      content: 'Mumbai, Maharashtra, India',
      link: null,
    },
  ];

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_gk-solutions-dev/artifacts/87lzsvrc_627675338_122100946359246119_1517692003938856282_n.jpg" 
                alt="GK Legal Solutions" 
                className="h-14 w-auto object-contain bg-white rounded-lg p-1"
              />
            </div>
            <p className="font-body text-slate-400 text-sm leading-relaxed mb-6">
              Your trusted partner for business registration, GST compliance, and legal services. 
              We help entrepreneurs start and grow their businesses with ease.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61587383587488" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/gk_legal_solutions?igsh=MWpjeWI5N2RhY3VydQ==" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/gklegalsolutions/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="font-body text-slate-400 text-sm hover:text-white hover:pl-2 transition-all flex items-center gap-2 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h3 className="font-heading text-lg font-semibold mt-8 mb-6">Our Services</h3>
            <ul className="space-y-3">
              {services.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="font-body text-slate-400 text-sm hover:text-white hover:pl-2 transition-all flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Services */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-6">Popular Services</h3>
            <ul className="space-y-3">
              {popularServices.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path}
                    className="font-body text-slate-400 text-sm hover:text-white hover:pl-2 transition-all flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a href="tel:+919820669943" className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors">
                  <Phone className="w-5 h-5 mt-0.5 text-primary" />
                  <div>
                    <p className="font-body text-sm font-medium text-white">Phone</p>
                    <p className="font-body text-sm">+91 98206 69943</p>
                  </div>
                </a>
              </li>
              <li>
                <a href="mailto:info@gklegalsolutions.com" className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors">
                  <Mail className="w-5 h-5 mt-0.5 text-primary" />
                  <div>
                    <p className="font-body text-sm font-medium text-white">Email</p>
                    <p className="font-body text-sm">info@gklegalsolutions.com</p>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <Clock className="w-5 h-5 mt-0.5 text-primary" />
                <div>
                  <p className="font-body text-sm font-medium text-white">Working Hours</p>
                  <p className="font-body text-sm">Mon - Sat: 10:30 AM - 7:00 PM</p>
                </div>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 mt-0.5 text-primary" />
                <div>
                  <p className="font-body text-sm font-medium text-white">Address</p>
                  <p className="font-body text-sm">Mumbai, Maharashtra, India</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-body text-slate-400 text-sm text-center md:text-left">
              © {currentYear} GK Legal Solutions. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="font-body text-slate-400 text-sm hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="font-body text-slate-400 text-sm hover:text-white transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
